module.exports = {
	"intro": [
		{
			"type": "text",
			"value": "\\**Container ships move around 90% of the world's cargo**. If the tag on your shirt doesn't match the place where you live, chances are it came to you via container ship. These enormous ocean-going vessels, the largest of which are **longer than the Empire State Building is tall**, are the **cheapest and most environmentally friendly** way we have to ship massive amounts of cargo. Without container ships, the globalized economy wouldn't exist. **Meanwhile the amount of cargo we're shipping across the world has tripled since 2000**."
		},
		{
			"type": "text",
			"value": "\\**And that may be a problem**. Though container ships are cleaner than planes or trucks for transporting cargo, **they're not clean**. They cause **environmental damage**, and emit gases that lead to **disability and death** in ports and coastal areas — **especially in Asia**."
		},
		{
			"type": "text",
			"value": "The **International Maritime Organization (IMO)**, the United Nations body charged with regulating the shipping industry, has made progress in pushing for **more efficient and less polluting ships**. The newest container ships emit less pollutants per kilogram of cargo shipped (when fully loaded) than ever before, but the improvements are **far from enough to offset the increase in shipping**. [IMO research](http://www.imo.org/en/OurWork/Environment/PollutionPrevention/AirPollution/Documents/Third%20Greenhouse%20Gas%20Study/GHG3%20Executive%20Summary%20and%20Report.pdf) shows **pollutants will continue to increase for the foreseeable future** unless something is done."
		},
		{
			"type": "text",
			"value": "As shipping reaches **unprecedented levels worldwide**, the open question is whether the **IMO and the international community** have the will to **contain the environmental fallout**."
		}
	],
	"shipping-and-your-health": [
		{
			"type": "text",
			"value": "If you live in a **major Asian port city**, or near the coast, you may not realize how much of the **pollution** you breathe is from the **shipping industry**."
		},
		{
			"type": "text",
			"value": "The **large container ships** that are a feature of Asia's coastal scenery send out plumes of **deadly particulate pollution**, including **acid-rain-causing sulfur oxides**. Particulate matter (PM) — especially fine particulate matter measuring less than 2.5 micrometers (PM2.5) — is particularly harmful to human health, causing **lung cancer and other cardiopulmonary problems**. It's also responsible for most of the haze that blots out skylines in most major Asian cities."
		},
		{
			"type": "text",
			"value": "Ships also send out nitrogen oxides which become ozone in the atmosphere. Ozone also causes lung cancer and cardiopulmonary problems."
		},
		{
			"type": "text",
			"value": "(We'll focus on particulates because there's more research available, but keep in mind when we estimate the number of deaths below we're ignoring the contribution of ozone.)"
		},
		{
			"type": "component",
			"value": "breakdown"
		},
		{
			"type": "text",
			"value": "If you live in **Hong Kong**, the world's 5th busiest port, approximately **42% of PM2.5 emissions you inhaled in 2013 came from the maritime sector**, according to the Hong Kong government's pollution inventory. And that proportion has been on the rise for a decade as road transport and electrical generation have cleaned up their emissions. Maritime activities are also the **largest source of sulfur and nitrogen oxide**."
		},
		{
			"type": "text",
			"value": "Dr. James Corbett of the University of Delaware, a leading authority on shipping emissions, calculated in [a 2007 study](http://pubs.acs.org/doi/abs/10.1021/es071686z) that shipping emissions are **responsible for 60,000 premature deaths per year worldwide. These deaths are concentrated in East Asia, South Asia and Europe**. His study also predicted a 40% rise in deaths attributable to shipping pollution between then and 2012. And according to Dr. Corbett, the death toll is likely **still rising**: \"As population increases and as there's an increase in port activity because of growth in trade, we would expect mortality rates to increase over time in some degree of proportionality.\""
		},
		{
			"type": "text",
			"value": "\\**Container ships usually burn cheap, high-sulfur residual fuel oil.** This fuel can **contain 3,500 times more sulfur than the diesel allowed for cars** in Japan, South Korea, and in the Chinese mainland after stricter limits come into force in 2017. **Refineries** sell these dirty tar-like fuels which are the **leftovers of the oil refining process** as a profitable side business."
		},
		{
			"type": "text",
			"value": "Because of health concerns, the IMO has set up several **emission control areas (ECAs)** to protect people living in coastal areas. ECAs are buffer areas around countries' coastlines where ships must switch to **cleaner low-sulfur fuels** (e.g. distillates or liquified natural gas) that emit less particulate matter. Ships can also install \"scrubbers\" which remove pollutants emitted from dirtier fuels after combustion."
		},
		{
			"type": "text",
			"value": "ECAs are created at the request of the affected countries. They cover most of the U.S. and Canadian coastlines, the English channel, and the Northern and Baltic Seas, **protecting the populations of North America and Europe from the dirtiest emissions**."
		},
		{
			"type": "text",
			"value": "The problem? **The world's 10 busiest ports are all in Asia.**"
		}
	],
	"shipping-health-solutions1": [
		{
			"type": "text",
			"value": "\\##### Baby Steps"
		},
		{
			"type": "text",
			"value": "For **Hong Kong** residents, there's some good news: the government **made a first move to limit fuel sulfur content starting in 2015**. Ships must switch to 0.5% sulfur fuel — five times less restrictive than European and North American ECAs which mandate 0.1% sulfur content. Unlike ECAs, sulfur limits apply only to ships when berthed, not when in transit."
		},
		{
			"type": "text",
			"value": "Still, according to Simon Ng, Chief Research Officer of local think tank Hong Kong Civic Exchange, \"It's a positive breakthrough because the government's never done anything to regulate ship emission in Hong Kong and also in Asia, [...] it's an important first step.\""
		},
		{
			"type": "text",
			"value": "In January this year, the **Chinese mainland also launched its own emission control zones** outside of the IMO framework, surrounding some of its largest ports in the **Pearl River Delta, Yangtze River Delta and Bohai Sea**. For the moment, these emission control zones rely on voluntary participation, but fuel sulfur limits will become stricter with time, and an 0.5% fuel sulfur limit will become mandatory for all ships in these zones in 2019."
		},
		{
			"type": "text",
			"value": "Currently no other Asian country has adopted mandatory limits, and **the IMO confirmed they have not received any formal requests for ECAs from any other countries**."
		},
		{
			"type": "text",
			"value": "([See the North American ECA request to see what one looks like](https://www.documentcloud.org/documents/2823776-North-America-Eca-Proposal.html). Warning: It's long.)"
		},
		{
			"type": "text",
			"value": "The International Chamber of Shipping, the sector’s principal trade association, said in an email, \"provided that the coastal states concerned follow the IMO process (i.e proving scientifically a compelling need, we have no objection to more ECAs.\""
		},
		{
			"type": "text",
			"value": "Mr. Ng hopes that the recent movement in China will have a **knock-on effect in other Asian countries like Japan, South Korea, and Singapore, as well as Malaysia and Indonesia**. \"Now that China has got its plan, and Hong Kong of course already had regulation, so I think there will be a trigger impact to the neighbouring areas as well.\""
		}
	],
	"shipping-health-solutions2": [
		{
			"type": "text",
			"value": "\\##### Sea Change - a global cap on sulfur"
		},
		{
			"type": "text",
			"value": "As part of the International Convention for the Prevention of Pollution from Ships (MARPOL), the IMO's members agreed to a **global 0.5% cap on sulfur content per volume for 2020**. This is **less restrictive than European and North American ECAs**, but would make Hong Kong and the Chinese mainland's current regulations redundant."
		},
		{
			"type": "text",
			"value": "In the short term, a global cap may be **a more promising avenue for saving lives**. Regulation that creates a **level playing field for all** sidesteps many of the difficulties that individual ports and countries face in regulating shipping. For example, ports may **avoid overly strict regulation for fear of sending traffic to competing ports**. Nearby countries may also **share responsability for major waterways**, making effective ECAs difficult to impose for individual countries. A global cap **avoids both these problems**."
		},
		{
			"type": "text",
			"value": "A [2009 study](http://pubs.acs.org/doi/abs/10.1021/es803224q) estimated that **a global 0.5% sulfur limit** would have begun **saving as many as 46,000 lives per year** if it had been implemented in 2012, with roughly **half of the avoided deaths in Asia**."
		},
		{
			"type": "component",
			"value": "battlequote"
		},
		{
			"type": "text",
			"value": "But there's a catch: the shipping industry pushed for a **loophole** to be written into MARPOL which gives the IMO the power to **delay the measure until 2025**. The IMO has until 2018 to make a decision about whether the industry is ready for a stricter global sulfur cap."
		},
		{
			"type": "text",
			"value": "Neither governments or industry dispute the effect of dirty fuels on human health. **What's up for debate is the timing, fuel availability and the price of adapting refineries**. \"It's cost.\" says Dr. Corbett, \"The maritime industry for the last 70-plus years has devoted its innovation and technology for engines and propulsion systems to be able to take advantage of the least costly energy resources that were available to them, these were these residual petroleum products.\""
		},
		{
			"type": "text",
			"value": "\\**The fuel price hike which may follow tighter regulation could also be difficult for the shipping industry to absorb**. The Baltic Dry Index, an economic indicator that tracks the going rate for shipping, hit an all-time low earlier this year (more likely due to the industry's oversupply of ships than a slowdown in the world economy). With prices so low, **shipping companies are struggling to make a profit**."
		},
		{
			"type": "text",
			"value": "\\**Maersk Line**, the world's largest shipping company, reported a **decline in first-quarter earnings** year-on-year for 2016. In a press release, it attributed the company's difficulties to the \"global rate war in container shipping\" and **challenging market conditions**. Signe Bruun Jensen, Maersk's Head of Sustainability, said in an email that Maersk supports global regulations mandating low-sulfur fuel but acknowledges that, \"Introducing a stricter vessel emission rule will bring challenges for shipping lines in a difficult market.\""
		},
		{
			"type": "text",
			"value": "According to Mr. Jensen, the **North American and European ECAs cost the company about $200 million extra per year**. The company was valued at about $185 billion by market cap at the time of publication."
		},
		{
			"type": "text",
			"value": "Bill Hemmings, Director of Aviation and Shipping at European NGO Transport & Environment, worries that **a delay now could lead to further delays in the future**: \"We fear if they succeed in delaying it to 2025, they might seek to delay it later on to 2030.\" The issue will be considered in October 2016. \"We're gearing up for the battle,\" he said."
		}
	],
	"shipping-and-your-world": [
		{
			"type": "text",
			"value": "Your **world and ecosystem are impacted by shipping pollution** in subtle ways that are **harder to quantify than human health**. These effects are **global and cross-border**, making action **dependent on different countries agreeing and acting in concert**. Tackling these issues **requires deep study** of aquatic ecosystems, water chemistry, and climate trends that takes **years to execute**. Often, the cost of acting is high. Environmentalists argue the price of inaction is even higher."
		}
	],
	"shipping-and-you": [
		{
			"type": "text",
			"value": "If shipping is to become more sustainable, governments and international organizations will need to be a part of the solution. **But what about you?** If shipping pollution, and the impact on port and coastal communities is an issue that is important to you, **what are your options to participate in the conversation?**"
		}
	],
	"hong-kong-profile": [
		{
			"type": "text",
			"value": "Hong Kong is the fifth busiest port in the world."
		},
		{
			"type": "component",
			"value": "breakdown"
		},
		{
			"type": "text",
			"value": "Hong Kong spent 58 days in 2015 with daily *average* fine suspended particle (PM2.5) levels classified as “unhealthy” or worse, and an additional 101 days classified as “unhealthy for sensitive groups,” according to U.S. Environmental Protection Agency categories, at at least one of Hong Kong’s Environmental Protection Department’s air quality monitoring stations."
		},
		{
			"type": "text",
			"value": "This is blabla."
		}
	],
	"singapore-profile": [
		{
			"type": "text",
			"value": "Singapore is the second busiest port in the world."
		}
	],
	"china-profile": [
		{
			"type": "text",
			"value": "##Shanghai, Shenzhen, etc."
		},
		{
			"type": "text",
			"value": "Mainland China has six of the ten busiest ports in the world. Shanghai is the busiest port in the world. Shenzhen is third."
		}
	],
	"busan-profile": [
		{
			"type": "text",
			"value": "##Busan, South Korea"
		},
		{
			"type": "text",
			"value": "Busan is the 5th busiest port in the world."
		}
	],
	"klang-profile": [
		{
			"type": "text",
			"value": "Port Kelang, Malaysia, is the 13th busiest port in the world."
		}
	],
	"globecards": [
		{
			"title": "Global Growth",
			"content": [
				{
					"type": "text",
					"value": "Worldwide **ship traffic grew fourfold in the 20 years** since 1992, according to [a 2014 study](http://onlinelibrary.wiley.com/doi/10.1002/2014GL061786/full)."
				},
				{
					"type": "text",
					"value": "Though traffic grew in **every ocean**, it grew most in the **Indian Ocean** and **Chinese seas**."
				},
				{
					"type": "text",
					"value": "Ship engine exhaust is a major source of air pollution in the open ocean. For example, the study found a **50% rise in nitrogen oxides** in the **Sri Lanka-Sumatra-China lane** in the studied period."
				},
				{
					"type": "text",
					"value": "(Spin the globe to expore worldwide ship traffic.)"
				}
			]
		},
		{
			"title": "Space Invaders",
			"content": [
				{
					"type": "text",
					"value": "Container ships need to be weighed down when empty, so they scoop up large amounts of ballast water, and dump it out when they're loaded with cargo."
				},
				{
					"type": "text",
					"value": "In the process **ships transport stowaway species from across the world**, which often have no natural predators in the environments in which they're introduced."
				},
				{
					"type": "text",
					"value": "These **invasive species can devastate ecosystems and fishing stocks**. The European Green Crab, an aggressive predator, has been carried in ballast water to six continents and has hurt the commercial shellfish industry."
				},
				{
					"type": "text",
					"value": "(Spin the globe to see where invasive species are being introduced.)"
				}
			]
		},
		{
			"title": "Acid Oceans",
			"content": [
				{
					"type": "text",
					"value": "The rise in **global carbon emissions has led to a more acidic ocean**. A [2013 study](http://onlinelibrary.wiley.com/doi/10.1002/grl.50521/full) found **nitrogen and sulfur oxides emitted by ships contribute to ocean acidification** in heavily trafficked sea lanes at a level comparable to that produced by carbon emissions."
				},
				{
					"type": "text",
					"value": "On a map of ocean acidity, many well-known shipping routes are readily identifiable."
				},
				{
					"type": "text",
					"value": "(Spin the globe to see where the ocean is acidifying.)"
				}
			]
		},
		{
			"title": "Shell Shock",
			"content": [
				{
					"type": "text",
					"value": "\\**A more acidic ocean prevents sea life from forming hard skeletons and shells**."
				},
				{
					"type": "text",
					"value": "It also leads to **coral bleaching** and the death of coral reefs."
				},
				{
					"type": "text",
					"value": "Coral reefs are essential to ocean ecosystems, and to the **food supplies of the over 100 million people** in Southeast Asia who depend on them for fish."
				},
				{
					"type": "text",
					"value": "(Spin the globe to see where the world's corals live.)"
				}
			]
		},
		{
			"title": "Other Pollutants",
			"content": [
				{
					"type": "text",
					"value": "Shipping also produces noise that harms ocean wildlife, emits water pollution, and contributes to climate change."
				},
				{
					"type": "text",
					"value": "The National Center for Ecological Analysis and Synthesis (NCEAS) studied the **total stress caused by human activities on the world's oceans**, including those caused by shipping and non-shipping activities (e.g. fishing). The globe shows the results of their study."
				},
				{
					"type": "text",
					"value": "(Spin the globe to see where where the world's oceans are under the most ecological stress.)"
				}
			]
		},
		{
			"title": "Compare the Maps",
			"content": [
				{
					"type": "text",
					"value": "Use the buttons below to see how the different maps compare with each other:"
				},
				{
					"type": "component",
					"value": "mapbuttons"
				}
			]
		}
	],
	"climate-talks": [
		{
			"type": "text",
			"value": "\\##### COP 21 Out"
		},
		{
			"type": "component",
			"value": "imopic"
		},
		{
			"type": "text",
			"value": "The 2015 **Paris Climate Conference (COP 21)**, the 21st conference of parties to the United Nations Framework Convention on Climate Change, **successfully advanced climate negotations** and reaffirmed the international community's goal to limit global temperatures to 1.5-2.0 degrees Celcius over the pre-industrial average. Participants pledged to reduce planet-warming carbon emissions, but notably **absent from the agreement was any mention of the shipping industry** or the aviation industry, whose contributions to global carbon emissions are growing."
		},
		{
			"type": "text",
			"value": "Before the conference, **IMO Secretary General** Koji Sekimizu [released a statement](https://www.documentcloud.org/documents/2823777-IMO-Sec-Gen-Statement-Shipping-and-Climate-Change.html) **discouraging world leaders from adopting a global cap on emissions of carbon dioxide or fiscal measures like a levy on fuel**, despite agreeing the industry had to reduce emissions. \"But who should decide on such measures and where should this be done? [...] I believe IMO is the **only** place to take this debate forward [emphasis his],\" said Mr. Sekimizu."
		},
		{
			"type": "text",
			"value": "While the industry currently produces only **around 3% of global carbon dioxide emissions, roughly the amount of Germany or Japan**, the IMO's [own research](http://www.imo.org/en/OurWork/Environment/PollutionPrevention/AirPollution/Documents/Third%20Greenhouse%20Gas%20Study/GHG3%20Executive%20Summary%20and%20Report.pdf) shows that the **industry's emissions could be more than three times as high by 2050**."
		},
		{
			"type": "text",
			"value": "Sekimizu also argued that whatever world leaders decided, \"they must first carefully consider the **impact** of those decisions in light of the **enormous contribution** that shipping makes to the world economy [emphasis his].\""
		},
		{
			"type": "text",
			"value": "The IMO Secretary General's apparent unwillingness to consider global carbon reductions was controversial because climate change treaties starting with the Kyoto Protocol have excluded shipping emissions from member states' targets, instead **charging the IMO with the responsibility to limit emissions**."
		},
		{
			"type": "text",
			"value": "According to Mr. Hemmings of Transport & Environment, \"The task was left to the IMO [...] to sort out, and they've been running around in circles for the last fifteen years or more.\""
		},
		{
			"type": "text",
			"value": "\\**The Marshall Islands**, a low-lying country which has suffered a recent string of natural disasters attributed to climate change, led **a coalition of South Pacific nations pleading for tough measures on shipping emissions** at COP 21. In a statement, the Marshall Island's foreign minister called Secretary General Sekimizu's attitude a \"danger to the planet\"."
		},
		{
			"type": "text",
			"value": "The IMO was given the satirical *Fossil of the Day* award by the Climate Action Network, a grouping of 950 non-governmental organizations from over 100 countries."
		},
		{
			"type": "text",
			"value": "While early versions of the document included references to the expected contribution of shipping and aviation, they were **scrubbed from the final text**. It is unlikely the Paris Agreement will achieve the goal of keeping the rise in global temperatures below two degrees Celcius above pre-industrial levels if shipping and aviation emissions continue on their current trajectory."
		}
	],
	"climate-talks-2": [
		{
			"type": "text",
			"value": "\\##### A New Hope"
		},
		{
			"type": "text",
			"value": "The IMO's Marine Environment Protection Committee had its 69th meeting at the end of April, in the **first test of the IMO's resolve to tackle greenhouse gas emissions since the Paris talks**. It was unlikely that an emissions reduction target would be decided, but there were expectations participants would agree to a **roadmap for eventually establishing a \"fair share\" target for the industry**."
		},
		{
			"type": "text",
			"value": "In an email, IMO Communications Officer Natasha Brown confirmed increased **public pressure to act**: \"You could surmise that the exclusion of international shipping (and aviation) from the final text of the Paris Agreement could be seen as increasing the pressure on IMO to act on GHG [greenhouse gases].\""
		},
		{
			"type": "component",
			"value": "facequote"
		},
		{
			"type": "text",
			"value": "Since the Paris talks, the IMO has sworn in a **new Secretary General, South Korea's Ki-tack Lim**. \"I see the promotion of sustainable shipping and sustainable maritime development as one of the major priorities of my tenure,\" said Mr. Lim in his opening statement to the committee."
		},
		{
			"type": "text",
			"value": "In the **months following COP 21**, Pacific nations, including the Solomon Islands and the Marshall Islands, **expanded their coalition** [calling for action](https://www.documentcloud.org/documents/2838086-MEPC-69-GHG-Proposal-Belgium-France-Germany.html) and now includes much of Europe, Mexico, Liberia and industry groups. The **International Chamber of Shipping**, an influential industry group, [accepted the need for \"decarbonization\" of the industry for the first time](https://www.documentcloud.org/documents/2838087-MEPC-69-GHG-Document-ICS.html). **The majority of member states and industry were pushing for action**."
		},
		{
			"type": "text",
			"value": "\"For the first time ever in this building you had Annex I countries, you had industry, and on top of that you had the major flag states [...] all lined up,\" said Mr. Hemmings of Transport & Environment, who was present at the meeting."
		},
		{
			"type": "text",
			"value": "The IMO's meeting **coincided with the signing of the Paris Agreement on Earth Day**. Despite high hopes **negotiations collapsed** due to a significant minority, composed of **BRICS countries led by China as well as the Cook Islands**. The IMO's new Secretary General was unable to keep the discussion from being **tabled until next October's meeting**."
		},
		{
			"type": "text",
			"value": "Environmentalist groups were quick to respond: \"The IMO has fallen flat on its face in the first test of its determination to tackle greenhouse gas emissions after Paris, unable even to agree to develop a work plan for reducing ship emissions,\" said John Maggs, Senior Policy Advisor at NGO Seas At Risk."
		},
		{
			"type": "text",
			"value": "Dr. Yubing Shi, a specialist in maritime law at China's Xiamen University, explains that developing **BRICS countries' shipping industries** may not yet have the technology and expertise to easily adapt to stricter standards, and **fear losing access to global ports** as a result of noncompliance. \"That's why China and also BRICS countries, those large developing countries, they don't hope the IMO will respond very quickly to the action that was achieved at the Paris Agreement,\" he said."
		},
		{
			"type": "text",
			"value": "\\**China and other BRICS countries may also prefer that market-based initiatives be handled at the U.N. level**, instead of exclusively at the IMO. The IMO applies rules equally to all ships, but the **U.N.'s climate change framework recognizes a different responsibility for developing and developed countries**. \"The two approaches have totally different implications for BRICS countries,\" said Dr. Shi."
		},
		{
			"type": "text",
			"value": "At the meeting, the IMO did approve a mandatory measure calling for ships to report their fuel consumption and emissions. It was also confirmed the Ballast Water Convention, which will oblige ships to treat their ballast water to avoid transporting invasive species, now has the support of 49 states representing 34.79% of the world's merchant tonnage — 35% is required for adoption."
		}
	],
	"imocards": [
		{
			"title": "Flag-of-convenience countries",
			"subtitle": "(Panama, Liberia, Marshall Islands, etc.)",
			"content": [
				{
					"type": "text",
					"value": "So-called flag-of-convenience countries were established as a means for shippers to avoid their own countries' labor laws. They are countries that have open registries (all ships must be registered with a country under maritime law), allowing foreign ships to fly their flag with little restriction. The largest, Panama, officially has the biggest merchant fleet in the world. Some of these countries outsource their registries to corporations. For example, the Marshall Islands registry is run out of Virginia, in the U.S."
				},
				{
					"type": "text",
					"value": "They have been the subject of criticism from legislators and labor unions since they were established beginning in the 1950s. In 2002, Franz Fischler, European Union Fisheries Commissioner said: \"The practice of flags of convenience, where owners register vessels in countries other than their own in order to avoid binding regulations or controls, is a serious menace to today’s maritime world.”"
				},
				{
					"type": "text",
					"value": "Around 70% of the world's merchant fleet (by tonnage) is registered under flags of convenience. Since the IMO is funded in proportion to the size of each state's merchant fleet, they are the primary funders of the IMO. Because all IMO conventions require the approval of at least 50% of the world's fleet by tonnage, together they hold veto-like power over the IMO's decisions."
				}
			]
		},
		{
			"title": "Industry Groups",
			"subtitle": "(ICS, WSC, ECSA, etc.)",
			"content": [
				{
					"type": "text",
					"value": "Groups such as the International Chamber of Shipping (ICS), the World Shipping Council (WSC) and the the European Community Shipowners' Associations (ECSA) represent the shipping industry's interests. They are not members of the IMO, which only admits governments, but still wield significant influence."
				},
				{
					"type": "text",
					"value": "ICS is among the most influential, accounting for roughly 80 percent of the world’s merchant tonnage. It favors global regulations through the IMO, opposing unilateral and regional action by governments according to its statement of purpose."
				},
				{
					"type": "text",
					"value": "Opposed to absolute carbon emission caps as recently as COP 21, ICS accepted the concept of \"decarbonization\" for the first time in its submission before the IMO's April 2016 meeting."
				},
				{
					"type": "text",
					"value": "According to greenhouse gas researcher Tristan Smith, author of the IMO's most recent greenhouse gas study, \"The reference [...] to decarbonization is therefore highly significant because it acknowledges [a reduction in greenhouse gas emissions] can only occur through absolute emission reduction.\""
				}
			]
		},
		{
			"title": "Non-governmental Organizations",
			"subtitle": "(CSC, WWF, FOEI, etc.)",
			"content": [
				{
					"type": "text",
					"value": "Environmental NGOs like the Clean Shipping Coalition (CSC) — whose membership includes Transport & Environment and Seas at Risk —, the World Wide Fund for Nature (WWF), and Friends of the Earth International (FOEI) advocate action to protect the environment and human health."
				},
				{
					"type": "text",
					"value": "The CSC, which exclusively focuses on shipping issues, is the most active at the IMO. According to its mission statement, \"It promotes policies aimed at the protection and restoration of the marine and atmospheric environment that are consistent with the safe operation of ships, sustainable development, social and economic justice, and human health [...] and is an invaluable conduit for informing the public about shipping issues and the vital work of the IMO.\""
				},
				{
					"type": "text",
					"value": "They do not have a vote at the IMO, but are accorded observer status by the organization."
				}
			]
		},
		{
			"title": "United States",
			"content": [
				{
					"type": "text",
					"value": "The U.S. has traditionally preferred to bet on technological solutions rather than tighter rules or market-based measures which resemble an international tax."
				},
				{
					"type": "text",
					"value": "U.S. Federal agencies have instead engaged with academia and industry to address marine issues such as ballast water treatment, port and vessel air emissions and the development of alternative fuel technologies."
				},
				{
					"type": "text",
					"value": "The U.S. has promoted the use of cleaner natural gas as a fuel, as well as the use of algae, sugar beets and fuel cells to reduce petroleum usage and cut greenhouse gas emissions. The recent greenhouse gas study commissioned by the IMO predicts new technology alone will not lead to an absolute emissions reduction for the shipping sector."
				}
			]
		},
		{
			"title": "European Union",
			"content": [
				{
					"type": "text",
					"value": "Europe has championed strict environmental regulations as well as market-based solutions to shipping emissions. This contrasts with the U.S., which has emphasized energy efficiency and innovation as the remedy to shipping's environmental woes."
				},
				{
					"type": "text",
					"value": "\"The E.U. has consistently been one of the leaders pushing IMO to address [greenhouse gas] emissions,\" said Tristan Smith, author of the IMO's most recent comprehensive greenhouse gas study, in an email."
				},
				{
					"type": "text",
					"value": "Europe was the first jurisdiction to adopt emission control areas. It also pioneered legislation to force ships that dock at European ports to monitor and report their emissions. The IMO has since adopted a similar measure which applies globally."
				}
			]
		},
		{
			"title": "BRICS countries",
			"subtitle": "(China, Russia, Brazil, India, South Africa)",
			"content": [
				{
					"type": "text",
					"value": "The so-called BRICS countries adhere to the principal of \"common but differentiated responsibilities\" which is enshrined in the Kyoto Protocol. China and its peers argue carbon emissions should be considered cumulatively since developed Europe and North America historically enjoyed a period of unfettered growth without emissions standards. Therefore, to apply the same standard to both developed and developing nations would unfairly stifle the latter's growth. This conflicts with the IMO's \"no more favourable treatment\" principle, which calls for all ships to be treated equally regardless of flag."
				},
				{
					"type": "text",
					"value": "According to a paper by Dr. Yubing Shi of Xiamen University's South China Sea Institute, China \"supports the leading role of the IMO in regulating technical related issues, but doubts the competency of the IMO to regulate [market-based emission reduction measures]\" and that such measures should involve the U.N. The paper goes on to say that if market-based measures were to be adopted, \"China’s view is that the [common but differentiated responsibilities] principle needs to be incorporated.\""
				}
			]
		}
	],
	"shipping-and-you-sections": [
		{
			"title": "Your Say",
			"image": "images/imo-meeting.jpg",
			"caption": "New Secretary General Ki-tack Lim presides over the 69th meeting of the IMO's Marine Environment Protection Committee in April.",
			"credit": "Courtesy of IMO",
			"content": [
				{
					"type": "text",
					"value": "The **progress the IMO has made in increasing shipping efficiency** should not be ignored. It has never been cleaner or more efficient to transport a kilogram of cargo."
				},
				{
					"type": "text",
					"value": "\\**Many environmental groups believe negotiations are too slow-moving**, but there are **significant political and technological hurdles** to overcome before concrete action can be taken."
				},
				{
					"type": "text",
					"value": "The good news? The aftermath of the Paris Climate Conference shows that **the IMO, industry, and individual governments can be moved by public pressure**. If you want to be a part of the conversation, **that's a good place to start**."
				}
			]
		},
		{
			"title": "Your Lifestyle",
			"video": "20160519am-shipping-kathmandu",
			"image": "images/video3-poster.jpg",
			"content": [
				{
					"type": "text",
					"value": "The global economy, international trade, shipping and general prosperity have historically marched in lock step. Climate change and environmental pressures risk breaking that link. Environmentalists argue that **a circular economy based on recycling, reuse, and local sourcing** is the only way to ensure further economic development does not come at the expense of prosperity. Bhutan Tuladhar, **U.N.-Habitat’s Regional Technical Advisor for South Asia**, practices what he preaches. In this video he invites us to his home in Kathmandu to see the simple measures he uses to **reduce his family's impact on the environment and the need for shipping goods around the world**."
				}
			]
		},
		{
			"title": "Your Dollar",
			"image": "images/shopper.jpg",
			"caption": "A busy shopping street in Shanghai.",
			"credit": "Johannes Eisele/Agence France-Presse",
			"content": [
				{
					"type": "text",
					"value": "There is currently **no global database of ship emissions** where you can look up which companies and ships are using cleaner fuels. Because no one knows who's emitting what, companies can't offer greener shipping to their clients and consumers can't vote with their purchases. And since comparison shopping of shippers is impossible, there is a lack of incentive for shipowners to adopt greener technologies."
				},
				{
					"type": "text",
					"value": "\\**But that's about to change**."
				},
				{
					"type": "text",
					"value": "The European Union has adopted a regulation obliging ships to disclose their fuel consumption and emissions. **Ships using European ports will have to prepare annual reports of their emissions**. This data will be **publically available starting in 2019**. This has motivated a similar (though less stringent) measure at the most recent meeting of the IMO. In the near future consumers may be able to make an **informed choice** about how they want their goods shipped."
				}
			]
		}
	]
};


//////////////////
// WEBPACK FOOTER
// ./src/content/main.archie
// module id = 10
// module chunks = 0